import React, { JSX, useEffect, useState } from 'react'
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { formatPrice } from 'lib/helpers'
import { AnalyticsMetafield, TotalsBase } from 'types/api'
import { exportIcon } from './Icons'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { stringify } from 'csv-stringify/sync'
import { ProductSalesTable } from './ProductSalesTable'
import { CountrySalesTable } from './CountrySalesTable'
import { ReferralPlatformTable } from './ReferralPlatformTable'
import { ReferringSitesTable } from './ReferringSitesTable'
import { CustomerTable } from './CustomerTable'
import { useIsFetching } from '@tanstack/react-query'

export interface SalesTableProps {
  products?: AnalyticsMetafield[]
  bundles?: AnalyticsMetafield[]
  filters: {
    productOption?: AnalyticsMetafield[]
    bundleOption?: AnalyticsMetafield[]
    range: {
      startDate: Date
      endDate: Date
    }
    project?: AnalyticsMetafield
  }
  token?: string
  role?: string
  client: AnalyticsMetafield | null
}

interface SortConfig {
  key: string
  direction: 'asc' | 'desc'
}

const SortIndicator = ({
  sortConfig,
  sortKey
}: {
  sortConfig: SortConfig | null
  sortKey: string
}): JSX.Element => {
  const getSortClass = (direction: 'asc' | 'desc'): string => {
    if (sortConfig?.key !== sortKey) return 'invisible'
    return sortConfig.direction === direction ? 'text-lm-text-primary dark:text-dm-text-primary' : 'text-lm-text-accent dark:text-dm-text-accent'
  }

  return (
    <span className='flex flex-col items-center justify-center leading-none'>
      <ChevronUpIcon className={`w-3 h-3 ${getSortClass('asc')}`} />
      <ChevronDownIcon className={`w-3 h-3 ${getSortClass('desc')}`} />
    </span>
  )
}

export const SortableHeader = ({
  first = false,
  label,
  sortKey,
  sortConfig,
  onSort
}: {
  first?: boolean
  label: string
  sortKey: string
  sortConfig: SortConfig | null
  onSort: (key: string) => void
}): JSX.Element => {
  const firstClass = first ? 'pl-2' : ''

  return (
    <th
      scope='col'
      onClick={() => onSort(sortKey)}
      className={`cursor-pointer sm:pl-4 py-4 pr-6 text-sm font-semibold tracking-wide md:table-cell lg:pr-20 text-left ${firstClass}`}
    >
      <div className='inline-flex items-center gap-1 px-2 py-1.5 rounded-md justify-start'>
        <span className='text-sm font-medium'>{label}</span>
        <SortIndicator sortConfig={sortConfig} sortKey={sortKey} />
      </div>
    </th>
  )
}

export const sortData = (sortConfig: SortConfig | null, data?: any[]): TotalsBase[] => {
  if (sortConfig == null || (data == null)) return []

  return [...data].sort((a, b) => {
    const aValue = a[sortConfig.key]
    const bValue = b[sortConfig.key]

    if (typeof aValue === 'string') {
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue as string)
        : (bValue as string).localeCompare(aValue)
    }

    return sortConfig.direction === 'asc'
      ? (aValue) - (bValue as number)
      : (bValue as number) - (aValue)
  })
}

export const SalesTableSection = ({ products, bundles, client, token, filters, role }: SalesTableProps): JSX.Element => {
  const isFetching = useIsFetching()
  const refreshing = isFetching > 0
  const [open, setOpen] = useState<boolean>(false)
  const [view, setView] = useState<'product' | 'referralPlatform' | 'country' | 'customer' | 'referringSite'>('product')
  const customerDataAllowed = role === 'admin' || role === 'analyst'

  return (
    <>
      <div className='w-full pt-4 mb-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 px-4 sm:px-0' data-testid='sales-table-section-wrapper'>
        <div className='grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-2'>
          <button
            data-testid='product-table-button'
            disabled={refreshing}
            type='button'
            onClick={() => setView('product')}
            className={`w-full sm:w-auto sm:min-w-[140px] px-3 py-1 sm:py-2 rounded-md text-sm font-semibold ${
        view === 'product'
          ? 'bg-brand text-white'
          : 'bg-lm-bg-main text-lm-text-primary dark:bg-dm-bg-main dark:text-dm-text-primary ring-1 shadow-xs dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 ring-black/5 ring-inset hover:bg-lm-bg-secondary dark:hover:bg-dm-bg-secondary'
      }`}
          >
            Product
          </button>
          <button
            data-testid='country-table-button'
            disabled={refreshing}
            type='button'
            onClick={() => setView('country')}
            className={`w-full sm:w-auto sm:min-w-[140px] px-3 py-1 sm:py-2 rounded-md text-sm font-semibold ${
        view === 'country'
          ? 'bg-brand text-white'
          : 'bg-lm-bg-main text-lm-text-primary dark:bg-dm-bg-main dark:text-dm-text-primary ring-1 shadow-xs dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 ring-black/5 ring-inset hover:bg-lm-bg-secondary dark:hover:bg-dm-bg-secondary'
      }`}
          >
            Country
          </button>

          <button
            data-testid='referral-platform-table-button'
            disabled={refreshing}
            type='button'
            onClick={() => setView('referralPlatform')}
            className={`w-full sm:w-auto sm:min-w-[140px] px-3 py-1 sm:py-2 rounded-md text-sm font-semibold ${
        view === 'referralPlatform'
          ? 'bg-brand text-white'
          : 'bg-lm-bg-main text-lm-text-primary dark:bg-dm-bg-main dark:text-dm-text-primary ring-1 shadow-xs dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 ring-black/5 ring-inset hover:bg-lm-bg-secondary dark:hover:bg-dm-bg-secondary'
      }`}
          >
            Referral Platform
          </button>

          <button
            data-testid='referring-site-table-button'
            disabled={refreshing}
            type='button'
            onClick={() => setView('referringSite')}
            className={`w-full sm:w-auto sm:min-w-[140px] px-3 py-1 sm:py-2 rounded-md text-sm font-semibold ${
        view === 'referringSite'
          ? 'bg-brand text-white'
          : 'bg-lm-bg-main text-lm-text-primary dark:bg-dm-bg-main dark:text-dm-text-primary ring-1 shadow-xs dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 ring-black/5 ring-inset hover:bg-lm-bg-secondary dark:hover:bg-dm-bg-secondary'
      }`}
          >
            Referring Site
          </button>

          {customerDataAllowed && (
            <button
              data-testid='customer-table-button'
              disabled={refreshing}
              type='button'
              onClick={() => setView('customer')}
              className={`w-full min-h-[48px] sm:min-h-[0px] sm:w-auto sm:min-w-[140px] px-3 py-1 sm:py-2 rounded-md text-sm font-semibold ${
          view === 'customer'
            ? 'bg-brand text-white'
            : 'bg-lm-bg-main text-lm-text-primary dark:bg-dm-bg-main dark:text-dm-text-primary ring-1 shadow-xs dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 ring-black/5 ring-inset hover:bg-lm-bg-secondary dark:hover:bg-dm-bg-secondary'
        }`}
            >
              Customer
            </button>
          )}

        </div>

        <div className='justify-self-stretch sm:justify-self-end'>

          <button
            data-testid='export-modal-button'
            disabled={refreshing}
            type='button'
            onClick={() => setOpen(true)}
            title='Export'
            className='w-full sm:w-auto rounded-md py-2 px-3 text-white shadow-xs dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 bg-brand hover:bg-brand/70'
          >
            {exportIcon}
          </button>
        </div>
      </div>

      {view === 'product' &&
        <ProductSalesTable products={products} bundles={bundles} setOpen={setOpen} open={open} token={token} client={client} filters={filters} />}
      {view === 'country' &&
        <CountrySalesTable setOpen={setOpen} open={open} token={token} client={client} filters={filters} />}
      {view === 'referralPlatform' &&
        <ReferralPlatformTable setOpen={setOpen} open={open} token={token} client={client} filters={filters} />}
      {view === 'referringSite' &&
        <ReferringSitesTable setOpen={setOpen} open={open} token={token} client={client} filters={filters} />}
      {(view === 'customer' && customerDataAllowed) &&
        <CustomerTable setOpen={setOpen} open={open} token={token} client={client} filters={filters} />}
    </>
  )
}

export interface ExportModalProps {
  tableType: 'product' | 'country' | 'referralPlatform' | 'referringSite' | 'customer'
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  data: any[]
}

const getFieldsForTableType = (tableType: string, data: any[]): Array<{ id: string, title: string, selected: boolean }> => {
  if (data?.length === 0) return []

  const sample = data[0]
  const commonFields: Array<{ id: string, title: string, selected: boolean }> = []

  switch (tableType) {
    case 'product':
      if (sample.productName != null) commonFields.push({ id: 'productName', title: 'Product Name', selected: true })
      break
    case 'country':
      if (sample.country != null) commonFields.push({ id: 'country', title: 'Country Code', selected: true })
      if (sample.countryName != null) commonFields.push({ id: 'countryName', title: 'Country Name', selected: true })
      break
    case 'referralPlatform':
      if (sample.referralPlatform != null) commonFields.push({ id: 'referralPlatform', title: 'Referral Platform', selected: true })
      break
    case 'referringSite':
      if (sample.referringSite != null) commonFields.push({ id: 'referringSite', title: 'Referring Site', selected: true })
      break
    case 'customer':
      if (sample.email != null) commonFields.push({ id: 'email', title: 'Email', selected: true })
      if (sample.customerFirst != null) commonFields.push({ id: 'customerFirst', title: 'First Name', selected: true })
      if (sample.customerLast != null) commonFields.push({ id: 'customerLast', title: 'Last Name', selected: true })
      if (sample.smsMarketingConsent != null) commonFields.push({ id: 'smsMarketingConsent', title: 'SMS Marketing', selected: false })
      if (sample.emailMarketingConsent != null) commonFields.push({ id: 'emailMarketingConsent', title: 'Email Marketing', selected: false })
      break
  }

  if (sample.quantity !== undefined) commonFields.push({ id: 'quantity', title: 'Quantity', selected: true })
  if (sample.sales !== undefined) commonFields.push({ id: 'sales', title: 'Sales', selected: true })

  return commonFields
}

export const ExportModal = ({ tableType, open, setOpen, data }: ExportModalProps): JSX.Element => {
  const [selectedFields, setSelectedFields] = useState<Array<{ id: string, title: string, selected: boolean }>>([])

  useEffect(() => {
    if (!open) return
    setSelectedFields(getFieldsForTableType(tableType, data))
  }, [tableType, open, data])

  const handleDownload = (): void => {
    const selected = selectedFields.filter(field => field.selected)
    const headers = selected.map(field => field.title)

    const records = data.map(item =>
      selected.map(field => {
        const value = item[field.id]
        return field.id === 'sales' ? formatPrice(value as number) : value
      })
    )

    const csv = stringify(records, {
      header: true,
      columns: headers
    })

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${tableType}_report.csv`
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
    setOpen(false)
  }
  return (
    <Dialog data-testid='export-dialog' open={open} onClose={setOpen} className='relative z-10'>
      <DialogBackdrop
        transition
        className='fixed inset-0 bg-lm-bg-primary/80 dark:bg-dm-bg-primary/80 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in'
      />
      <div className='fixed inset-0 z-10 w-screen overflow-y-auto'>
        <div className='flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0'>
          <DialogPanel
            transition
            className='relative transform overflow-hidden rounded-lg bg-lm-bg-main dark:bg-black px-4 pt-5 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in my-8 w-full max-w-sm p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95'
          >
            <div className='mt-3 sm:mt-5'>
              <DialogTitle as='h3' className='text-xl text-center mb-4'>
                Export report
              </DialogTitle>
              <ExportForm selectedFields={selectedFields} setSelectedFields={setSelectedFields} />
            </div>
            <div className='mt-5 sm:mt-6 flex gap-3'>
              <button
                type='button'
                onClick={() => setOpen(false)}
                title='Cancel'
                data-testid='cancel-download'
                className='inline-flex w-full justify-center rounded-md p-2 outline-lm-text-accent dark:outline-dm-text-accent outline-1 hover:bg-lm-bg-secondary dark:hover:bg-dm-bg-secondary flex items-center justify-center text-sm'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleDownload}
                title='Download'
                data-testid='download-csv'
                className='inline-flex w-full justify-center rounded-md rounded-md p-2 text-white bg-brand hover:bg-brand/70 flex items-center justify-center text-sm'
              >
                Download
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}

interface ExportFormProps {
  selectedFields: Array<{ id: string, title: string, selected: boolean }>
  setSelectedFields: React.Dispatch<React.SetStateAction<Array<{ id: string, title: string, selected: boolean }>>>
}

const ExportForm = ({ selectedFields, setSelectedFields }: ExportFormProps): JSX.Element => {
  const toggleField = (id: string): void => {
    setSelectedFields(prev =>
      prev.map(field =>
        field.id === id ? { ...field, selected: !field.selected } : field
      )
    )
  }

  return (
    <fieldset>
      <legend className='text-sm text-center text-lm-text-secondary dark:text-dm-text-secondary mb-6'>Include fields:</legend>
      {selectedFields.map((field) => (
        <div key={`field-${field.id}`} className='flex items-center gap-3 py-3'>
          <div className='group grid size-4 grid-cols-1'>
            <input
              checked={field.selected}
              onChange={() => toggleField(field.id)}
              id={`${field.id}`}
              name={`${field.id}`}
              type='checkbox'
              className='col-start-1 row-start-1 appearance-none rounded-sm border border-gray-300 dark:border-gray-700 checked:border-brand checked:bg-brand indeterminate:border-brand indeterminate:bg-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 dark:border-gray-700 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto'
            />
            <svg
              fill='none'
              viewBox='0 0 14 14'
              className='pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-disabled:stroke-gray-950/25'
            >
              <path
                d='M3 8L6 11L11 3.5'
                strokeWidth={2}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='opacity-0 group-has-checked:opacity-100'
              />
              <path
                d='M3 7H11'
                strokeWidth={2}
                strokeLinecap='round'
                strokeLinejoin='round'
                className='opacity-0 group-has-indeterminate:opacity-100'
              />
            </svg>
          </div>
          <div className='min-w-0 flex-1 text-sm/6'>
            <label htmlFor={`${field.id}`} className='font-medium select-none'>
              {field.title}
            </label>
          </div>
        </div>
      ))}
    </fieldset>
  )
}
